import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { selectIsCompanyMode } from "../../Branches/redux/branches.slice.ts"
import { isProductTourStepPermitted, visibleProductTourSteps } from "../content/product-tour-filter.ts"
import { type TourRect } from "./geometry.ts"
import { measureElement } from "./measure-element.ts"
import { type ProductTourRuntimeContext, type ProductTourStep } from "./product-tour.types.ts"
import {
  resolveFirstAccessibleLead,
  resolveFirstAccessibleProject
} from "./resolve-tour-record.ts"
import { waitForTarget } from "./wait-for-target.ts"

export type ProductTourStatus = "idle" | "loading" | "ready" | "fallback"

interface ProductTourContextValue {
  isActive: boolean
  status: ProductTourStatus
  activeStep: ProductTourStep | null
  stepIndex: number
  totalSteps: number
  targetRect: TourRect | null
  /** True once the fallback in effect is genuinely "you don't have one of
   * these yet" (requiresRecord resolved to null) — vs. a real record whose
   * target just didn't render in time. Purely informational for the
   * overlay; the copy itself already comes from the right step either way. */
  /** Starts the full tour from its first step, or — pass a `PRODUCT_TOUR`
   * step id — jumps straight to that one contextual step (e.g. a Learn
   * EZBuild lesson's "Take Interactive Tour" launching directly at
   * "project-milestones" rather than replaying Home→Leads→... first). An
   * id that isn't found, or isn't part of this user's permission-filtered
   * steps, falls back to the first step rather than silently doing nothing. */
  start: (stepId?: string) => void
  next: () => void
  back: () => void
  skip: () => void
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null)

const measure = (el: Element): TourRect => measureElement(el)

const emptyRuntimeContext: ProductTourRuntimeContext = { leadId: null, projectId: null }

/**
 * Every step resolves through the exact same SHOW→NAVIGATE→WAIT→HIGHLIGHT
 * procedure regardless of direction — `back()` is not a separate "undo," it
 * is `goToStep(index - 1)`, the identical function `next()` calls with
 * `index + 1`. That's what makes Back genuinely restore the real route, tab
 * and record instead of just decrementing a counter.
 */
export const ProductTourProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)
  // Company HQ vs a single branch workspace — Company HQ has no
  // Leads/Projects navigation at all, so every step built around them
  // (`workspaceScope: "branch"`) can never apply there, and a handful of
  // HQ-only steps (its own nav items, the workspace-selector explainer)
  // apply only there.
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const currentWorkspace: "hq" | "branch" = isCompanyMode ? "hq" : "branch"
  // Read fresh inside `goToStep` without making its identity depend on
  // permissions changing — `stepsRef` is filtered once at `start()`, but a
  // step already in that list can still lose its own gate mid-tour (an
  // admin revokes access while the user is on step 7 of 12); this is what
  // lets goToStep notice that on the very next transition instead of
  // navigating into a page the user can no longer open. `workspaceRef`
  // exists for the identical reason: the global workspace selector can
  // change mid-tour too.
  const pagesRef = useRef(pages)
  const codesRef = useRef(codes)
  const workspaceRef = useRef(currentWorkspace)
  pagesRef.current = pages
  codesRef.current = codes
  workspaceRef.current = currentWorkspace

  const [isActive, setIsActive] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [status, setStatus] = useState<ProductTourStatus>("idle")
  const [targetRect, setTargetRect] = useState<TourRect | null>(null)

  // Refs, not state — these drive an in-flight async resolution and must
  // read as current-at-call-time values inside it, not a stale closure.
  const stepsRef = useRef<ProductTourStep[]>([])
  // The workspace `stepsRef` was filtered for — a full HQ↔branch flip
  // invalidates most/all of that list at once (unlike a single permission
  // being revoked), so it's handled as its own guard below rather than
  // folded into goToStep's per-step skip loop.
  const startedWorkspaceRef = useRef<"hq" | "branch">(currentWorkspace)
  const runtimeCtxRef = useRef<ProductTourRuntimeContext>(emptyRuntimeContext)
  const unavailableRef = useRef<Set<"lead" | "project">>(new Set())
  // Set only once a step has actually rendered the honest "you don't have
  // one of these yet" card for a record kind (not merely once the record is
  // known-unavailable) — open-lead/open-project both declare `requiresRecord`
  // purely to pre-fetch it, but resolve to real, always-shown content of
  // their own either way, so they must never be skipped by this.
  const fallbackShownRef = useRef<Set<"lead" | "project">>(new Set())
  // Bumped on every goToStep call; an in-flight wait checks it's still the
  // current request before touching state, so a fast double-click can't let
  // a stale resolution overwrite a newer one.
  const requestTokenRef = useRef(0)

  const resolveRecordIfNeeded = useCallback(
    async (kind: "lead" | "project"): Promise<string | null> => {
      const key = kind === "lead" ? "leadId" : "projectId"
      const current = runtimeCtxRef.current[key]
      if (current !== null) return current
      // null already means "resolved, none exist" — only fetch once, ever,
      // per tour run, whatever comes back.
      if (unavailableRef.current.has(kind)) return null

      const id = kind === "lead" ? await resolveFirstAccessibleLead() : await resolveFirstAccessibleProject()
      runtimeCtxRef.current = { ...runtimeCtxRef.current, [key]: id }
      if (id === null) unavailableRef.current.add(kind)
      return id
    },
    []
  )

  const goToStep = useCallback(
    async (rawIndex: number, direction: 1 | -1) => {
      const steps = stepsRef.current
      if (steps.length === 0) return

      // Skip a step that has nothing of its own to show for an
      // already-known-missing record, once that honest "you don't have one
      // yet" card has already been shown for it — repeating an identical
      // fallback adds nothing. A step whose own route never depends on the
      // record (open-lead/open-project, always the real list) is never
      // skipped by this, even though it also declares `requiresRecord` (to
      // pre-fetch it for the steps after it). A step whose own `requiredPage`
      // no longer passes against the *current* permissions is skipped the
      // same way — `stepsRef` was filtered once at `start()`, so this only
      // fires when permissions changed mid-tour.
      let index = rawIndex
      while (index >= 0 && index < steps.length) {
        const candidate = steps[index]
        if (
          !isProductTourStepPermitted(candidate.requiredPage, pagesRef.current, codesRef.current)
        ) {
          index += direction
          continue
        }
        if (candidate.requiresRecord && unavailableRef.current.has(candidate.requiresRecord)) {
          const routeIfUnavailable = candidate.resolveRoute?.(runtimeCtxRef.current) ?? null
          if (routeIfUnavailable === null) {
            if (!fallbackShownRef.current.has(candidate.requiresRecord)) break
            index += direction
            continue
          }
        }
        break
      }
      index = Math.min(Math.max(index, 0), steps.length - 1)

      // Every remaining step (in the direction travelled) lost its
      // permission — nothing left to show. End the tour cleanly rather than
      // clamping onto a step the user can no longer open.
      if (
        !isProductTourStepPermitted(
          steps[index]?.requiredPage,
          pagesRef.current,
          codesRef.current
        )
      ) {
        requestTokenRef.current += 1
        setIsActive(false)
        setStatus("idle")
        setTargetRect(null)
        setStepIndex(0)
        return
      }

      const token = ++requestTokenRef.current
      const step = steps[index]
      setStepIndex(index)
      setStatus("loading")
      setTargetRect(null)

      // This is a multi-step async chain (record fetch, route resolution, DOM wait); `catch` below resolves to the same "fallback" status a missing target uses, so a throw here can never leave the user stuck at "loading" with dead Back/Skip/Next.
      try {
        if (step.requiresRecord) {
          await resolveRecordIfNeeded(step.requiresRecord)
          if (requestTokenRef.current !== token) return
        }

        const route = step.resolveRoute?.(runtimeCtxRef.current) ?? null
        if (requestTokenRef.current !== token) return

        if (route === null) {
          // requiresRecord resolved to null — this is the one step that owns
          // telling the user "you don't have one of these yet". The previous
          // step already navigated to the real list page this step would
          // otherwise have opened from, so no navigation happens here — just
          // spotlight this step's own fallback entry point, if it named one.
          if (step.fallbackTarget) {
            const fallbackEl = document.querySelector(
              `[data-guide-target="${step.fallbackTarget}"]`
            )
            setTargetRect(fallbackEl ? measure(fallbackEl) : null)
          }
          if (step.requiresRecord) fallbackShownRef.current.add(step.requiresRecord)
          setStatus("fallback")
          return
        }

        const [routePath, routeSearch] = route.split("?")
        const alreadyThere =
          location.pathname === routePath &&
          (location.search.replace(/^\?/, "") || "") === (routeSearch ?? "")
        if (!alreadyThere) navigate(route)

        if (!step.target) {
          setStatus("ready")
          return
        }

        const result = await waitForTarget(step.target, {
          isCancelled: () => requestTokenRef.current !== token
        })
        if (requestTokenRef.current !== token) return

        if (result.status === "found") {
          setTargetRect(measure(result.element))
          setStatus("ready")
          return
        }
        if (result.status === "cancelled") return

        // Timeout: the record was real but the target never arrived (a Lead
        // whose stage redirects `?tab=documents` back to Overview, a slow
        // permission gate, …). Fall back to this step's own fallback target
        // if it named one, else render its own copy centered — never a
        // silent skip to the next step.
        if (step.fallbackTarget) {
          const fallbackEl = document.querySelector(
            `[data-guide-target="${step.fallbackTarget}"]`
          )
          setTargetRect(fallbackEl ? measure(fallbackEl) : null)
        }
        setStatus("fallback")
      } catch {
        if (requestTokenRef.current !== token) return
        setTargetRect(null)
        setStatus("fallback")
      }
    },
    [navigate, location.pathname, location.search, resolveRecordIfNeeded]
  )

  const start = useCallback(
    (stepId?: string) => {
      const steps = visibleProductTourSteps(pages, codes, workspaceRef.current)
      if (steps.length === 0) return
      stepsRef.current = steps
      startedWorkspaceRef.current = workspaceRef.current
      runtimeCtxRef.current = emptyRuntimeContext
      unavailableRef.current = new Set()
      fallbackShownRef.current = new Set()
      setIsActive(true)
      // `start` is commonly passed straight through as an onClick handler
      // (existing call sites do this), which would hand it a MouseEvent as
      // this argument — guard with a real type check, not just truthiness,
      // so that never gets misread as a step id.
      const startIndex =
        typeof stepId === "string" ? Math.max(steps.findIndex((step) => step.id === stepId), 0) : 0
      void goToStep(startIndex, 1)
    },
    [pages, codes, goToStep]
  )

  const stop = useCallback(() => {
    requestTokenRef.current += 1
    setIsActive(false)
    setStatus("idle")
    setTargetRect(null)
    setStepIndex(0)
  }, [])

  // Switching the global workspace selector mid-tour (Company HQ ↔ a
  // branch) invalidates the running tour's own step list outright — ending
  // cleanly here, rather than trying to keep going, is what lets the user's
  // next `start()` call (from wherever Guide offers it in that workspace)
  // build the correct list for where they actually are. Never auto-starts
  // the other tour itself — that would be switching FOR the user.
  useEffect(() => {
    if (isActive && currentWorkspace !== startedWorkspaceRef.current) {
      stop()
    }
  }, [isActive, currentWorkspace, stop])

  const next = useCallback(() => {
    if (stepIndex >= stepsRef.current.length - 1) {
      stop()
      return
    }
    void goToStep(stepIndex + 1, 1)
  }, [stepIndex, goToStep, stop])

  const back = useCallback(() => {
    void goToStep(stepIndex - 1, -1)
  }, [stepIndex, goToStep])

  const value = useMemo<ProductTourContextValue>(
    () => ({
      isActive,
      status,
      activeStep: isActive ? (stepsRef.current[stepIndex] ?? null) : null,
      stepIndex,
      totalSteps: stepsRef.current.length,
      targetRect,
      start,
      next,
      back,
      skip: stop
    }),
    [isActive, status, stepIndex, targetRect, start, next, back, stop]
  )

  return (
    <ProductTourContext.Provider value={value}>{children}</ProductTourContext.Provider>
  )
}

const NOOP: ProductTourContextValue = {
  isActive: false,
  status: "idle",
  activeStep: null,
  stepIndex: 0,
  totalSteps: 0,
  targetRect: null,
  start: () => {},
  next: () => {},
  back: () => {},
  skip: () => {}
}

export const useProductTour = (): ProductTourContextValue =>
  useContext(ProductTourContext) ?? NOOP
